import _ from 'underscore';
// import { convertAspectRatioToDecimal } from '@bingads-webui-campaign/media-common-utils';
import { ImageExperienceImage, constants as C } from '@bingads-webui-campaign-react/image-experience-model';

import {
  getCenterCroppedSettings,
  processCroppedImages
} from './get-cropped-image';
import {
  validateSmartCropSettings,
  validateExpandedCropSettings,
} from './validate-crop-settings';

const ORIGINAL_IMAGE = 'OriginalImage';
/**
 * Return the image objects
 *
 * @param {Object} originalImageToExpand - ImageExperienceImage Object
 * @param {string} originalImageToExpand.Src - source of the image
 * @param {Number} originalImageToExpand.width - width of the image
 * @param {Number} originalImageToExpand.height - height of the image
 * @param {Object} isMultimediaAd - true if current is mma
 * @param {Object} isMultimediaAdinMSANEnabled - true if current is msan mma
 * @returns {Array} - Array of image objects, ImageExperienceImage[]
 */
export const getExpandedImages = ({
  originalImageToExpand,
  isMultimediaAd,
  isMultimediaAdinMSANEnabled,
  isCombinedMsanAndMmaAspectRatios,
  isMMAV2Enabled,
  isBoost200x100ImageEnabled,
  isBoostNewAspectRatioSupportEnabled,
  logger,
  isPMaxAspectRatiosV2Enabled,
}) => {
  const aspectRatios = C.getAspectRatios({
    isMultimediaAd,
    isMultimediaAdinMSANEnabled,
    isCombinedMsanAndMmaAspectRatios,
    imageOption: C.imageOptionKeys.Image,
    isMMAV2Enabled,
    isBoost200x100ImageEnabled,
    isBoostNewAspectRatioSupportEnabled,
    isPMaxAspectRatiosV2Enabled,
  });
  const defaultRatio = C.getDefaultRatio(originalImageToExpand.Width, originalImageToExpand.Height, aspectRatios);

  const expandedImageObj = {
    expandedImageHeight: originalImageToExpand.expandedImageHeight,
    expandedImageWidth: originalImageToExpand.expandedImageWidth,
    expandedImageCropSettings: originalImageToExpand.expandedImageCropSettings,
    originalImageCropSettings: originalImageToExpand.originalImageCropSettings || originalImageToExpand.cropSettings,
    imageExpansionType: originalImageToExpand.imageExpansionType,
    paddingColor: originalImageToExpand.paddingColor,
  };

  const {
    expandedImageUrl,
    expandedImageHeight,
    expandedImageWidth,
    expandedImageCropSettings, // [{AspectRatio:double, CropSetting: {SourceHeight, SourceWidth, SourceX, SourceY}}]
    originalImageCropSettings, // [{AspectRatio:double, CropSetting: {SourceHeight, SourceWidth, SourceX, SourceY}}]
    paddingColor,
    imageExpansionType,
  } = expandedImageObj || {};

  // combine original and expanded crop settings by aspect ratio
  const cropMetaData = {};
  _.each(originalImageCropSettings, (crop) => {
    const croppingType = imageExpansionType === C.ImageExpansionType.Outpainting || crop.AspectRatio === ORIGINAL_IMAGE ? C.CroppingType.CenterCrop : C.CroppingType.SmartCrop;
    cropMetaData[crop.AspectRatio] = { originCrop: { ...crop.CropSetting, CroppingType: croppingType } };
  });
  _.each(expandedImageCropSettings, (crop) => {
    cropMetaData[crop.AspectRatio] = { ...cropMetaData[crop.AspectRatio], expandedCrop: crop.CropSetting, CroppingType: C.CroppingType.CenterCrop };
  });
  _.each(aspectRatios, (placementSetting) => {
    const key = placementSetting.type === ORIGINAL_IMAGE ? ORIGINAL_IMAGE : placementSetting.aspectRatio;
    cropMetaData[key] = { ...cropMetaData[key], placementSetting };
  });

  const imageList = _.compact(_.map(cropMetaData, ({ originCrop, expandedCrop, placementSetting }) => {
    const expandedImageData = {
      id: originalImageToExpand.Id,
      idString: originalImageToExpand.IdString,
      src: originalImageToExpand.Src,
      width: originalImageToExpand.width,
      height: originalImageToExpand.height,
      source: originalImageToExpand.source,

      // image extension properties
      imageExpansionType,
      expandedImageUrl,
      expandedImageWidth,
      expandedImageHeight,
      expandedImageCropSettings: expandedCrop,
      originalImageCropSettings: originCrop,
      paddingColor,
    };

    const curAspectRatio = _.propertyOf(placementSetting)('aspectRatio');
    const ratioDecimalStr = `${curAspectRatio}`;
    const minSize = _.propertyOf(placementSetting)('minSize') || [];

    expandedImageData.type = _.propertyOf(placementSetting)('type');
    expandedImageData.associationType = _.propertyOf(placementSetting)('associationType');
    expandedImageData.isLogo = _.propertyOf(placementSetting)('isLogo');
    expandedImageData.isPortrait = _.propertyOf(placementSetting)('isPortrait');
    expandedImageData.isIncluded = defaultRatio && ratioDecimalStr === _.result(defaultRatio, 'aspectRatio').toString();


    // validate smart croping on original image
    if (!originCrop || !validateSmartCropSettings({
      cropSettings: originCrop,
      originalImage: originalImageToExpand,
      aspectRatio: curAspectRatio,
      minWidth: minSize.length === 2 && minSize[0],
      minHeight: minSize.length === 2 && minSize[1],
      smartCropActivity: logger,
    })) {
      // fill missing aspect ratios with central crop
      expandedImageData.originalImageCropSettings = getCenterCroppedSettings({
        SourceX: 0, SourceY: 0, SourceWidth: originalImageToExpand.Width, SourceHeight: originalImageToExpand.Height,
      }, curAspectRatio, false, true);

      if (expandedImageData.type !== ORIGINAL_IMAGE) {
        // this ratios are the reason why we need to expand the image
        logger.trace(`Invalid original crop settings for aspect ratio: ${curAspectRatio}, imageSrc: ${originalImageToExpand.Src}, ${JSON.stringify(originCrop)}, ${JSON.stringify(expandedImageData.originalImageCropSettings)}`);
      }
    }

    // validate expanded crop settings on expanded image
    if (!expandedCrop || !validateExpandedCropSettings({
      imageExpansionType,
      expandedImageWidth,
      expandedImageHeight,
      expandedImageUrl,
      cropSettings: expandedCrop,
      aspectRatio: curAspectRatio,
      minWidth: minSize.length === 2 && minSize[0],
      minHeight: minSize.length === 2 && minSize[1],
      logger,
    })) {
      expandedImageData.expandedImageCropSettings = undefined;

      if (expandedImageData.type !== ORIGINAL_IMAGE) {
        // but this should not happen, because this is what we try to fix by image expansion
        logger.error(`Invalid expanded crop settings for aspect ratio: ${curAspectRatio}, imageSrc: ${originalImageToExpand.Src}, ${JSON.stringify(expandedCrop)}, ${JSON.stringify(expandedImageData.expandedImageCropSettings)}, ${JSON.stringify(expandedImageData.expandedImageCropSettings)}`);
      }
    }

    return new ImageExperienceImage(expandedImageData);
  }));

  processCroppedImages({
    croppedImages: imageList,
    isMultimediaAdinMSANEnabled,
    aspectRatios,
  });

  return imageList;
};
